<template>
  <div class="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 flex">
    <!-- Mobile Header -->
    <div class="mobile-header lg:hidden">
      <div class="flex items-center justify-between px-4 py-3 bg-white/80 backdrop-blur-xl border-b border-gray-200">
        <div class="flex items-center space-x-3">
          <div class="w-8 h-8 bg-gradient-to-br from-blue-600 to-purple-600 rounded-lg flex items-center justify-center">
            <svg class="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </div>
          <div>
            <div class="font-bold text-gray-900 text-sm">GeoPresence</div>
            <div class="text-xs text-gray-500">Super Admin</div>
          </div>
        </div>
        <button
          @click="toggleMobileSidebar"
          class="p-2 rounded-lg hover:bg-gray-100 transition-colors duration-200"
        >
          <svg class="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </button>
      </div>
    </div>

    <!-- Sidebar -->
    <aside 
      class="sidebar"
      :class="{ 
        'sidebar-collapsed': sidebarCollapsed,
        'sidebar-mobile-open': mobileMenuOpen
      }"
    >
      <!-- Logo -->
      <div class="sidebar-header">
        <div class="flex items-center space-x-3">
          <div class="w-12 h-12 bg-gradient-to-br from-blue-600 to-purple-600 rounded-2xl flex items-center justify-center shadow-lg">
            <svg class="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </div>
          <div v-if="!sidebarCollapsed" class="animate-fade-in-right">
            <div class="font-bold text-gray-900 text-lg">GeoPresence</div>
            <div class="text-sm text-gray-500">Super Admin Portal</div>
          </div>
        </div>
        
        <button
          @click="toggleSidebar"
          class="hidden lg:flex p-2 rounded-lg hover:bg-gray-100 transition-all duration-200"
        >
          <svg class="w-5 h-5 text-gray-600 hover:text-gray-800 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" :d="sidebarCollapsed ? 'M9 5l7 7-7 7' : 'M15 19l-7-7 7-7'" />
          </svg>
        </button>
        
        <!-- Mobile Close Button -->
        <button
          @click="closeMobileSidebar"
          class="lg:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors duration-200"
        >
          <svg class="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <!-- Navigation -->
      <nav class="sidebar-nav">
        <div class="space-y-1">
          <!-- Dashboard -->
          <router-link
            to="/"
            class="nav-item"
            :class="{ 'nav-item-active': $route.name === 'Dashboard' }"
            @click="closeMobileSidebar"
          >
            <div class="nav-icon-wrapper">
              <svg class="nav-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2H5a2 2 0 00-2 2v10z" />
              </svg>
            </div>
            <span v-if="!sidebarCollapsed" class="nav-label">Tableau de Bord</span>
            <div v-if="sidebarCollapsed" class="nav-tooltip">Tableau de Bord</div>
          </router-link>

          <!-- Tenants -->
          <div class="nav-group">
            <router-link
              to="/tenants"
              class="nav-item"
              :class="{ 'nav-item-active': $route.path.startsWith('/tenants') }"
              @click="closeMobileSidebar"
            >
              <div class="nav-icon-wrapper">
                <svg class="nav-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-4m-5 0H9m0 0H5m0 0h2M7 7h10M7 11h4m6 0h4" />
                </svg>
              </div>
              <span v-if="!sidebarCollapsed" class="nav-label">Entreprises</span>
              <div v-if="sidebarCollapsed" class="nav-tooltip">Entreprises</div>
            </router-link>
            
            <router-link
              v-if="!sidebarCollapsed"
              to="/tenants/create"
              class="nav-subitem"
              :class="{ 'nav-subitem-active': $route.name === 'CreateTenant' }"
              @click="closeMobileSidebar"
            >
              <span class="nav-sublabel">+ Nouvelle Entreprise</span>
            </router-link>
          </div>

          <!-- Analytics - COMPLETELY HIDDEN -->
          <template v-if="false">
            <router-link
              to="/analytics"
              class="nav-item"
              :class="{ 'nav-item-active': $route.name === 'Analytics' }"
              @click="closeMobileSidebar"
            >
              <div class="nav-icon-wrapper">
                <svg class="nav-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <span v-if="!sidebarCollapsed" class="nav-label">Analytics</span>
              <div v-if="sidebarCollapsed" class="nav-tooltip">Analytics</div>
            </router-link>
          </template>

          <!-- Billing - Temporairement désactivé -->
          <!--
          <router-link
            to="/billing"
            class="nav-item"
            :class="{ 'nav-item-active': $route.name === 'Billing' }"
            @click="closeMobileSidebar"
          >
            <div class="nav-icon-wrapper">
              <svg class="nav-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
              </svg>
            </div>
            <span v-if="!sidebarCollapsed" class="nav-label">Facturation</span>
            <div v-if="sidebarCollapsed" class="nav-tooltip">Facturation</div>
          </router-link>
          -->

        </div>
      </nav>

      <!-- User Menu -->
      <div class="sidebar-footer">
        <div class="user-menu">
          <div class="flex items-center space-x-3">
            <div class="w-10 h-10 bg-gradient-to-br from-blue-600 to-purple-600 rounded-xl flex items-center justify-center shadow-lg">
              <span class="text-white font-bold text-sm">SA</span>
            </div>
            <div v-if="!sidebarCollapsed" class="flex-1 min-w-0">
              <div class="font-semibold text-gray-900 truncate">Super Admin</div>
              <div class="text-xs text-gray-500">admin@geopresence.com</div>
            </div>
            <button
              v-if="!sidebarCollapsed"
              @click="logout"
              class="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all duration-200"
              title="Se déconnecter"
            >
              <svg class="w-4 h-4 hover:scale-110 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
            </button>
          </div>
          
          <!-- Collapsed State User Menu -->
          <div v-if="sidebarCollapsed" class="user-menu-collapsed">
            <button
              @click="logout"
              class="w-full p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all duration-200 relative"
              title="Se déconnecter"
            >
              <svg class="w-4 h-4 mx-auto hover:scale-110 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
              <div class="nav-tooltip">Se déconnecter</div>
            </button>
          </div>
        </div>
      </div>
    </aside>

    <!-- Main Content -->
    <main class="main-content" :class="{ 'main-content-mobile': mobileMenuOpen }">
      <div class="main-content-wrapper">
        <router-view />
      </div>
    </main>

    <!-- Mobile Overlay -->
    <div
      v-if="mobileMenuOpen"
      @click="closeMobileSidebar"
      class="mobile-overlay"
    ></div>

    <!-- Modal de confirmation de déconnexion -->
    <ConfirmationModal
      :isVisible="showLogoutModal"
      type="warning"
      title="Se déconnecter"
      message="Êtes-vous sûr de vouloir vous déconnecter ? Vous serez redirigé vers la page de connexion."
      confirmText="Se déconnecter"
      cancelText="Annuler"
      @confirm="confirmLogout"
      @cancel="cancelLogout"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import ConfirmationModal from '../ConfirmationModal.vue'
import apiService from '@/services/api'

const router = useRouter()

// State
const sidebarCollapsed = ref(false)
const mobileMenuOpen = ref(false)

// Computed
const isMobile = computed(() => window.innerWidth < 1024)

// Methods
const toggleSidebar = () => {
  sidebarCollapsed.value = !sidebarCollapsed.value
  localStorage.setItem('sidebar-collapsed', sidebarCollapsed.value.toString())
}

const toggleMobileSidebar = () => {
  mobileMenuOpen.value = !mobileMenuOpen.value
}

const closeMobileSidebar = () => {
  mobileMenuOpen.value = false
}

// État pour le modal de déconnexion
const showLogoutModal = ref(false)

const logout = async () => {
  showLogoutModal.value = true
}

const confirmLogout = async () => {
  try {
    // Appeler l'API de déconnexion pour invalider le token côté serveur
    await apiService.logout()
  } catch (error) {
    console.warn('Erreur lors de la déconnexion backend:', error)
    // Continuer même si l'API échoue (nettoyage local prioritaire)
  } finally {
    // Nettoyage complet du localStorage (données sensibles)
    const sensitiveKeys = [
      'super_admin_token',
      'super_admin_user',
      'sidebar-collapsed'  // Optionnel: préférences utilisateur
    ]

    sensitiveKeys.forEach(key => {
      localStorage.removeItem(key)
    })

    // Fermer le modal
    showLogoutModal.value = false

    // Redirection sécurisée vers la page de login
    router.push('/login')
  }
}

const cancelLogout = () => {
  showLogoutModal.value = false
}

const handleResize = () => {
  if (window.innerWidth >= 1024) {
    mobileMenuOpen.value = false
  }
}

// Lifecycle
onMounted(() => {
  // Restore sidebar state
  const saved = localStorage.getItem('sidebar-collapsed')
  if (saved) {
    sidebarCollapsed.value = saved === 'true'
  }

  window.addEventListener('resize', handleResize)
})

onUnmounted(() => {
  window.removeEventListener('resize', handleResize)
})
</script>

<style scoped>
/* Mobile Header */
.mobile-header {
  @apply fixed top-0 left-0 right-0 z-50;
}

/* Sidebar */
.sidebar {
  @apply fixed left-0 top-0 h-full bg-white/95 backdrop-blur-xl border-r border-gray-200/80 shadow-2xl transition-all duration-300 z-50;
  width: 280px;
}

.sidebar-collapsed {
  width: 72px;
}

.sidebar-header {
  @apply flex items-center justify-between p-6 border-b border-gray-100/80;
}

.sidebar-nav {
  @apply flex-1 px-4 py-6 overflow-y-auto;
}

.sidebar-footer {
  @apply p-4 border-t border-gray-100/80;
}

/* Navigation Items */
.nav-item {
  @apply flex items-center px-4 py-3 text-gray-700 rounded-xl hover:bg-gray-50 transition-all duration-200 relative;
}

.nav-item-active {
  @apply bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg;
}

.nav-item-active:hover {
  @apply from-blue-700 to-purple-700;
}

.nav-icon-wrapper {
  @apply flex items-center justify-center w-10 h-10 rounded-lg transition-all duration-200;
}

.nav-item:hover .nav-icon-wrapper {
  @apply bg-white/10;
}

.nav-item-active .nav-icon-wrapper {
  @apply bg-white/20;
}

.nav-icon {
  @apply w-5 h-5 flex-shrink-0;
}

.nav-label {
  @apply font-medium ml-3 transition-all duration-200;
}

.nav-tooltip {
  @apply absolute left-full ml-2 px-3 py-2 bg-gray-900 text-white text-sm rounded-lg opacity-0 invisible transition-all duration-200 whitespace-nowrap z-50;
}

.nav-item:hover .nav-tooltip {
  @apply opacity-100 visible;
}

/* Sub Navigation */
.nav-group {
  @apply space-y-1;
}

.nav-subitem {
  @apply flex items-center pl-14 py-2.5 text-sm text-gray-600 rounded-lg hover:bg-gray-50 transition-all duration-200;
}

.nav-subitem-active {
  @apply text-blue-600 bg-blue-50 font-medium;
}

.nav-sublabel {
  @apply font-medium;
}

/* Separators */
.nav-separator {
  @apply mt-8 mb-4;
}

.nav-separator-label {
  @apply text-xs font-bold text-gray-400 uppercase tracking-wider px-4;
}

.nav-separator-collapsed {
  @apply mt-6 mb-2 mx-4 h-px bg-gray-200;
}

/* User Menu */
.user-menu {
  @apply bg-gray-50/80 rounded-xl p-3;
}

.user-menu-collapsed {
  @apply mt-3;
}

/* Main Content */
.main-content {
  @apply flex-1 min-h-screen transition-all duration-300;
  margin-left: 280px;
}

.main-content-wrapper {
  @apply min-h-screen;
}

.sidebar-collapsed + .main-content {
  margin-left: 72px;
}

/* Mobile Overlay */
.mobile-overlay {
  @apply fixed inset-0 bg-black/50 z-40 lg:hidden backdrop-blur-sm;
}

/* Mobile Responsive */
@media (max-width: 1023px) {
  .sidebar {
    @apply transform -translate-x-full;
    width: 280px;
  }
  
  .sidebar-mobile-open {
    @apply translate-x-0;
  }
  
  .main-content {
    margin-left: 0;
    padding-top: 60px;
  }
  
  .main-content-mobile {
    @apply overflow-hidden;
  }
  
  .sidebar-collapsed + .main-content {
    margin-left: 0;
  }
  
  .nav-tooltip {
    @apply hidden;
  }
}

/* Animations */
@keyframes slideIn {
  from {
    transform: translateX(-100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.animate-fade-in-right {
  animation: fadeIn 0.3s ease-out;
}

/* Hover Effects */
.nav-item:hover {
  @apply transform scale-105;
}

.nav-item-active:hover {
  @apply transform scale-105;
}

/* Focus States */
.nav-item:focus {
  @apply outline-none ring-2 ring-blue-500 ring-opacity-50;
}

/* Smooth Scrolling */
.sidebar-nav {
  scrollbar-width: thin;
  scrollbar-color: #cbd5e1 transparent;
}

.sidebar-nav::-webkit-scrollbar {
  width: 4px;
}

.sidebar-nav::-webkit-scrollbar-track {
  background: transparent;
}

.sidebar-nav::-webkit-scrollbar-thumb {
  background-color: #cbd5e1;
  border-radius: 2px;
}

.sidebar-nav::-webkit-scrollbar-thumb:hover {
  background-color: #94a3b8;
}

/* Enhanced Visual Effects */
.sidebar {
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
}

.nav-item-active {
  box-shadow: 0 10px 25px -3px rgba(59, 130, 246, 0.3);
}

/* Responsive Typography */
@media (max-width: 640px) {
  .nav-label {
    @apply text-sm;
  }
  
  .nav-sublabel {
    @apply text-xs;
  }
}</style>